import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowLeft, 
    faHeart, 
    faShieldAlt, 
    faCheck,
    faBank,
    faMobile,
    faGift,
    faUser,
    faEnvelope,
    faPhone,
    faCertificate,
    faClock
} from '@fortawesome/free-solid-svg-icons';


// EmailJS import (npm install @emailjs/browser 필요)
import emailjs from '@emailjs/browser';
import api from '../api/api';
import './MainPage.css';

const public_key_emailjs = process.env.REACT_APP_EMAILJS_PUBLIC_KEY



// 관리자 이메일 알림 함수
const sendAdminNotification = async (donationData, campaign, amount) => {
    const templateParams = {
        donor_name: donationData.isAnonymous ? '익명 기부자' : donationData.donorName,
        donor_email: donationData.email,
        donor_phone: donationData.phone,
        donation_amount: new Intl.NumberFormat('ko-KR').format(amount) + '원',
        campaign_title: campaign.title,
        campaign_hospital: campaign.hospital,
        payment_method: donationData.paymentMethod === 'bank' ? '계좌이체 (무통장입금)' : '간편결제',
        is_recurring: donationData.isRecurring ? '정기후원 (월단위)' : '일시 기부',
        donation_message: donationData.message || '(응원 메시지 없음)',
        commemorative_info: donationData.commemorativeType ? 
            `${donationData.commemorativeType} (${donationData.commemorativeDate})` : '(기념일 없음)',
        request_time: new Date().toLocaleString('ko-KR'),
        transaction_id: 'REQ' + Date.now(),
        to_email: 'contact@tchelp.org'
    };

    try {
        await emailjs.send(
            'service_z49fe3i', // EmailJS Service ID
            'template_2nozqq6', // EmailJS Tmplate ID
            templateParams,
            public_key_emailjs  // EmailJS public key
        );
        
        console.log('관리자 알림 이메일 발송 성공');
        return true;
    } catch (error) {
        console.error('관리자 알림 이메일 발송 실패:', error);
        // 이메일 실패해도 기부 신청은 계속 진행
        return false;
    }
};

const DEFAULT_IMPACT_LABELS = {
    10000: '응급처치 키트 1세트',
    30000: '수술용 장갑 100박스',
    50000: '의료진 교육비 1일분',
    100000: 'CT 장비 부품 1개'
};

/** 직접입력 10만원 초과 시 키트 환산 단위 (1만원 = 1세트) */
const KIT_CONVERSION_UNIT = 10000;
const CUSTOM_AMOUNT_KIT_THRESHOLD = 100000;

const formatKitImpactMessage = (amount) => {
    const kits = Math.floor(amount / KIT_CONVERSION_UNIT);
    if (kits <= 0) return '';
    return `응급처치 키트 ${kits}세트`;
};

const DonatePage = () => {
    const { t } = useTranslation();
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Form data
    const [donationData, setDonationData] = useState({
        amount: '',
        customAmount: '',
        donorName: '',
        email: '',
        phone: '',
        isAnonymous: false,
        isRecurring: false,
        paymentMethod: 'bank',
        message: '',
        commemorativeType: '',
        commemorativeDate: ''
    });


    useEffect(() => {
        // 실제 캠페인 API 호출로 변경
        const fetchCampaign = async () => {
            try {
                setLoading(true);
                if (campaignId) {
                    // URL 파라미터에 campaign ID가 있으면 해당 캠페인 조회
                    const campaignData = await api.getCampaign(campaignId);
                    setCampaign(campaignData);
                } else {
                    // campaign ID가 없으면 첫 번째 캠페인 사용 (또는 일반 기부)
                    const campaigns = await api.getCampaigns();
                    if (campaigns && campaigns.length > 0) {
                        setCampaign(campaigns[0]);
                    } else {
                        setCampaign(null);
                    }
                }
            } catch (error) {
                console.error('캠페인 로딩 실패:', error);
                setCampaign(null);
            } finally {
                setLoading(false);
            }
        };

        fetchCampaign();
    }, [campaignId]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('ko-KR').format(amount) + '원';
    };

    const progressPercentage = campaign ? Math.round((campaign.current_amount / campaign.target_amount) * 100) : 0;

    const predefinedAmounts = [10000, 30000, 50000, 100000];

    const handleAmountSelect = (amount) => {
        setDonationData(prev => ({
            ...prev,
            amount: amount,
            customAmount: amount === 'custom' ? prev.customAmount : ''
        }));
    };

    const handleInputChange = (field, value) => {
        setDonationData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getCurrentAmount = () => {
        return donationData.amount === 'custom' ? parseInt(donationData.customAmount) || 0 : donationData.amount;
    };

    const getAmountImpactLabel = (amt) => {
        if (!campaign) return '';
        if (campaign.impact) {
            const v = campaign.impact[amt] ?? campaign.impact[String(amt)];
            if (v) return v;
        }
        return DEFAULT_IMPACT_LABELS[amt] || '';
    };

    const getImpactMessage = () => {
        const amount = getCurrentAmount();
        if (!campaign || !amount) return '';

        // 직접입력 + 10만원 초과: CT 대신 응급처치 키트 개수로 환산
        if (donationData.amount === 'custom' && amount > CUSTOM_AMOUNT_KIT_THRESHOLD) {
            return formatKitImpactMessage(amount);
        }

        const impactData = campaign.impact || DEFAULT_IMPACT_LABELS;
        const impacts = Object.entries(impactData)
            .sort(([a], [b]) => parseInt(b) - parseInt(a));

        for (const [threshold, impact] of impacts) {
            if (amount >= parseInt(threshold, 10)) {
                return impact;
            }
        }
        return '';
    };

    const handleNext = () => {
        if (step < 4) {
            setStep(step + 1);
        }
    };

    const handlePrev = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    };

    const handleSubmit = async () => {
        // Prepare donation data
        const submissionData = {
            campaign_id: campaignId ? parseInt(campaignId) : null,
            amount: getCurrentAmount(),
            donor_name: donationData.isAnonymous ? 'Anonymous' : donationData.donorName,
            email: donationData.email,
            phone: donationData.phone,
            payment_method: donationData.paymentMethod,
            is_recurring: donationData.isRecurring,
            message: donationData.message,
            commemorative_type: donationData.commemorativeType,
            commemorative_date: donationData.commemorativeDate,
            is_anonymous: donationData.isAnonymous,
            status: 'pending_payment' // 입금 대기 상태
        };
        
        console.log('Saving donation to database:', submissionData);
        
        // Send admin notification email
        const emailSent = await sendAdminNotification(donationData, campaign, getCurrentAmount());
        if (emailSent) {
            console.log('관리자 알림 발송 완료');
        } else {
            console.log('관리자 알림 발송 실패 (기부 신청은 정상 처리됨)');
        }

        // Save to database via API
        try {
            const response = await api.createDonation(submissionData);
            
            console.log('Database response:', response);
            
            // Store transaction ID for reference
            const transactionId = response.transaction_id || response.id || 'REQ' + Date.now();
            sessionStorage.setItem('lastTransactionId', transactionId);
            
            setStep(5); // Success step
        } catch (error) {
            console.error('Error saving donation:', error);
            // Still proceed to success page even if API fails (for demo purposes)
            // In production, you would handle this differently
            const fallbackTransactionId = 'REQ' + Date.now();
            sessionStorage.setItem('lastTransactionId', fallbackTransactionId);
            setStep(5); // Still show success
        }
    };

    const canProceedStep1 = getCurrentAmount() >= 1000;
    const canProceedStep2 = donationData.donorName.trim() && donationData.email.trim() && donationData.phone.trim();
    const canProceedStep3 = donationData.paymentMethod;

    if (loading) {
        return (
            <div style={loadingStyle}>
                <div style={spinnerStyle}></div>
                <p>기부 정보를 불러오는 중...</p>
            </div>
        );
    }

    if (!campaign) {
        return (
            <div style={errorStyle}>
                <h2>캠페인을 찾을 수 없습니다</h2>
                <Link to="/" style={linkStyle}>홈으로 돌아가기</Link>
            </div>
        );
    }

    return (
        <div style={pageStyle}>
            {/* Header */}
            <header style={headerStyle}>
                <div style={containerStyle}>
                    <button onClick={() => navigate(-1)} style={backButtonStyle}>
                        <FontAwesomeIcon icon={faArrowLeft} /> 뒤로가기
                    </button>
                    <Link to="/" className="logo">
                        <img src="/Logo5.png" alt={t('nav.logoAlt')} className="logo-image" />
                        <span className="logo-text">{t('nav.organizationName')}</span>
                    </Link>
                    <div style={{width: '100px'}}></div> {/* Spacer */}
                </div>
            </header>

            <main style={mainStyle}>
                <div style={mainContainerStyle}>
                    <div style={contentStyle}>
                        <div style={{
                            ...sidebarColumnStyle,
                            ...(step >= 2 ? sidebarColumnNonStickyStyle : {})
                        }}>
                            <div style={sidebarPanelStyle}>
                            <div style={campaignTopWrapStyle}>
                                <div style={campaignCardInnerStyle}>
                                    <div style={campaignHeaderStyle}>
                                        <span style={urgencyBadgeStyle}>{campaign.urgency}</span>
                                        <h3 style={campaignTitleStyle}>{campaign.title}</h3>
                                        <p style={hospitalNameStyle}>{campaign.hospital}</p>
                                    </div>

                                    <div style={progressSectionStyle}>
                                        <div style={progressInfoStyle}>
                                            <span style={currentAmountStyle}>{formatCurrency(campaign.current_amount)}</span>
                                            <span style={targetAmountStyle}>목표: {formatCurrency(campaign.target_amount)}</span>
                                        </div>
                                        <div style={progressBarContainerStyle}>
                                            <div style={{...progressBarStyle, width: `${progressPercentage}%`}}></div>
                                        </div>
                                        <p style={progressTextStyle}>{progressPercentage}% 달성</p>
                                    </div>

                                    {getCurrentAmount() > 0 && (
                                        <div style={impactBoxStyle}>
                                            <FontAwesomeIcon icon={faGift} style={{color: '#22c55e', marginRight: '8px'}} />
                                            <span>{formatCurrency(getCurrentAmount())}로 <strong>{getImpactMessage()}</strong>을 지원할 수 있어요!</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={stepsStyle}>
                                {[1, 2, 3, 4].map((stepNum) => (
                                    <div key={stepNum} style={{
                                        ...stepStyle,
                                        ...(stepNum <= step ? activeStepStyle : {}),
                                        ...(stepNum < step ? completedStepStyle : {})
                                    }}>
                                        <div style={{
                                            ...stepNumberStyle,
                                            backgroundColor: stepNum < step
                                                ? '#22c55e'
                                                : stepNum === step
                                                    ? '#a65353'
                                                    : '#cbd5e1'
                                        }}>
                                            {stepNum < step ? <FontAwesomeIcon icon={faCheck} /> : stepNum}
                                        </div>
                                        <span style={stepLabelStyle}>
                                            {stepNum === 1 && '기부금액'}
                                            {stepNum === 2 && '기부자정보'}
                                            {stepNum === 3 && '결제방법'}
                                            {stepNum === 4 && '최종확인'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            </div>
                        </div>

                        <div style={formSectionStyle}>
                            {step === 1 && (
                                <div style={stepContentStyle}>
                                    <h2 style={stepTitleStyle}>기부 금액을 선택해주세요</h2>
                                    <p style={stepDescStyle}>소중한 마음이 생명을 구하는 힘이 됩니다</p>
                                    
                                    <div style={amountGridStyle}>
                                        {predefinedAmounts.map(amount => (
                                            <button
                                                key={amount}
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => handleAmountSelect(amount)}
                                                style={{
                                                    ...amountButtonStyle,
                                                    ...(donationData.amount === amount ? selectedAmountStyle : {})
                                                }}
                                            >
                                                <span style={amountValueStyle}>{formatCurrency(amount)}</span>
                                                <span style={amountImpactStyle}>{getAmountImpactLabel(amount)}</span>
                                            </button>
                                        ))}
                                        
                                        <button
                                            type="button"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleAmountSelect('custom')}
                                            style={{
                                                ...amountButtonStyle,
                                                ...(donationData.amount === 'custom' ? selectedAmountStyle : {})
                                            }}
                                        >
                                            <span style={amountValueStyle}>직접입력</span>
                                            <span style={amountImpactStyle}>원하는 금액으로</span>
                                        </button>
                                    </div>

                                    {donationData.amount === 'custom' && (
                                        <div style={customAmountStyle}>
                                            <input
                                                type="number"
                                                placeholder="기부 금액을 입력하세요"
                                                value={donationData.customAmount}
                                                onChange={(e) => handleInputChange('customAmount', e.target.value)}
                                                style={customInputStyle}
                                                min="1000"
                                                step="1000"
                                            />
                                            <span style={currencyLabelStyle}>원</span>
                                        </div>
                                    )}

                                    {/*<div style={recurringOptionStyle}>
                                        <label style={checkboxLabelStyle}>
                                            <input
                                                type="checkbox"
                                                checked={donationData.isRecurring}
                                                onChange={(e) => handleInputChange('isRecurring', e.target.checked)}
                                                style={checkboxStyle}
                                            />
                                            <span style={checkboxTextStyle}>
                                                정기후원 (매월 자동 기부) 
                                                <small style={checkboxSubtextStyle}> - 언제든 해지 가능</small>
                                            </span>
                                        </label>
                                    </div>*/}
                                </div>
                            )}

                            {step === 2 && (
                                <div style={stepContentStyle}>
                                    <h2 style={stepTitleStyle}>기부자 정보를 입력해주세요</h2>
                                    <p style={stepDescStyle}>기부금 영수증 발급을 위해 정확한 정보를 입력해주세요</p>
                                    
                                    <div style={formGroupStyle}>
                                        <label style={labelStyle}>
                                            <FontAwesomeIcon icon={faUser} style={iconStyle} />
                                            성명 *
                                        </label>
                                        <input
                                            type="text"
                                            value={donationData.donorName}
                                            onChange={(e) => handleInputChange('donorName', e.target.value)}
                                            placeholder="실명을 입력해주세요"
                                            style={inputStyle}
                                            required
                                        />
                                    </div>

                                    <div style={formGroupStyle}>
                                        <label style={labelStyle}>
                                            <FontAwesomeIcon icon={faEnvelope} style={iconStyle} />
                                            이메일 *
                                        </label>
                                        <input
                                            type="email"
                                            value={donationData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            placeholder="example@email.com"
                                            style={inputStyle}
                                            required
                                        />
                                    </div>

                                    <div style={formGroupStyle}>
                                        <label style={labelStyle}>
                                            <FontAwesomeIcon icon={faPhone} style={iconStyle} />
                                            연락처 *
                                        </label>
                                        <input
                                            type="tel"
                                            value={donationData.phone}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            placeholder="010-0000-0000"
                                            style={inputStyle}
                                            required
                                        />
                                    </div>

                                    <div style={anonymousOptionStyle}>
                                        <label style={checkboxLabelStyle}>
                                            <input
                                                type="checkbox"
                                                checked={donationData.isAnonymous}
                                                onChange={(e) => handleInputChange('isAnonymous', e.target.checked)}
                                                style={checkboxStyle}
                                            />
                                            <span style={checkboxTextStyle}>
                                                익명 기부 (기부자 명단에 '익명'으로 표시)
                                            </span>
                                        </label>
                                    </div>

                                    <div style={commemorativeStyle}>
                                        <h4 style={subTitleStyle}>기념일 기부 (선택사항)</h4>
                                        <div style={commemorativeGridStyle}>
                                            <select
                                                value={donationData.commemorativeType}
                                                onChange={(e) => handleInputChange('commemorativeType', e.target.value)}
                                                style={selectStyle}
                                            >
                                                <option value="">기념일 선택</option>
                                                <option value="birthday">생일</option>
                                                <option value="anniversary">기념일</option>
                                                <option value="memorial">추모</option>
                                                <option value="celebration">축하</option>
                                            </select>
                                            
                                            {donationData.commemorativeType && (
                                                <input
                                                    type="date"
                                                    value={donationData.commemorativeDate}
                                                    onChange={(e) => handleInputChange('commemorativeDate', e.target.value)}
                                                    style={dateInputStyle}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div style={formGroupStyle}>
                                        <label style={labelStyle}>응원 메시지 (선택사항)</label>
                                        <textarea
                                            value={donationData.message}
                                            onChange={(e) => handleInputChange('message', e.target.value)}
                                            placeholder="의료진과 환자들에게 전할 응원의 메시지를 남겨주세요"
                                            style={textareaStyle}
                                            rows="3"
                                        />
                                    </div>
                                </div>
                            )}

                            {step === 3 && (
                                <div style={stepContentStyle}>
                                    <h2 style={stepTitleStyle}>결제 방법을 선택해주세요</h2>
                                    <p style={stepDescStyle}>안전하고 편리한 결제 시스템으로 보호됩니다</p>
                                    
                                    <div style={paymentGridStyle}>
                                        <label style={{
                                            ...paymentMethodStyle,
                                            ...(donationData.paymentMethod === 'bank' ? selectedPaymentStyle : {})
                                        }}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="bank"
                                                checked={donationData.paymentMethod === 'bank'}
                                                onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                                                style={{display: 'none'}}
                                            />
                                            <FontAwesomeIcon icon={faBank} style={paymentIconStyle} />
                                            <span style={paymentLabelStyle}>계좌이체</span>
                                            <span style={paymentDescStyle}>무통장 입금</span>
                                            <span style={{...paymentBadgeStyle, backgroundColor: '#22c55e', color: 'white'}}>추천</span>
                                        </label>

                                        <label style={{
                                            ...paymentMethodStyle,
                                            ...(donationData.paymentMethod === 'mobile' ? selectedPaymentStyle : {})
                                        }}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="mobile"
                                                checked={donationData.paymentMethod === 'mobile'}
                                                onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                                                style={{display: 'none'}}
                                            />
                                            <FontAwesomeIcon icon={faMobile} style={paymentIconStyle} />
                                            <span style={paymentLabelStyle}>간편결제</span>
                                            <span style={paymentDescStyle}>카카오페이, 토스페이</span>
                                            <span style={{...paymentBadgeStyle, backgroundColor: '#f59e0b', color: 'white'}}>인기</span>
                                        </label>
                                    </div>

                                    {donationData.paymentMethod === 'bank' && (
                                        <div style={bankInfoStyle}>
                                            <h4 style={bankInfoTitleStyle}>
                                                <FontAwesomeIcon icon={faBank} style={{marginRight: '8px', color: '#a65353'}} />
                                                무통장 입금 안내
                                            </h4>
                                            <div style={bankDetailsStyle}>
                                                <div style={bankDetailRowStyle}>
                                                    <span style={bankLabelStyle}>은행명:</span>
                                                    <span style={bankValueStyle}>국민은행</span>
                                                </div>
                                                <div style={bankDetailRowStyle}>
                                                    <span style={bankLabelStyle}>계좌번호:</span>
                                                    <span style={bankValueStyle}>268801-04-262502</span>
                                                </div>
                                                <div style={bankDetailRowStyle}>
                                                    <span style={bankLabelStyle}>예금주:</span>
                                                    <span style={bankValueStyle}>한국외상센터지원단체</span>
                                                </div>
                                                <div style={bankDetailRowStyle}>
                                                    <span style={bankLabelStyle}>입금자명:</span>
                                                    <span style={bankValueStyle}>{donationData.donorName || '입력하신 성명'}</span>
                                                </div>
                                            </div>
                                            <p style={bankNoteStyle}>
                                                ※ 입금 후 자동으로 기부 완료 처리됩니다<br/>
                                                ※ 입금자명을 정확히 입력해주세요<br/>
                                                ※ 데모용 계좌번호입니다
                                            </p>
                                        </div>
                                    )}

                                    {donationData.paymentMethod === 'mobile' && (
                                        <div style={mobilePaymentStyle}>
                                            <h4 style={mobilePaymentTitleStyle}>간편결제 선택</h4>
                                            <div style={mobilePaymentGridStyle}>
                                                <button style={mobilePaymentButtonStyle}>
                                                    <img src="https://developers.kakao.com/assets/img/about/logos/kakaopay/kakaopay_logo_yellow.png" 
                                                         alt="카카오페이" 
                                                         style={{height: '30px'}} />
                                                </button>
                                                <button style={mobilePaymentButtonStyle}>
                                                    <img src="https://static.toss.im/icons/png/4x/logo-toss-pay-blue.png" 
                                                         alt="토스페이" 
                                                         style={{height: '30px'}} />
                                                </button>
                                            </div>
                                            <p style={mobilePaymentNoteStyle}>
                                                ※ 선택하신 간편결제 서비스로 연결됩니다<br/>
                                                ※ 데모 버전에서는 실제 결제가 진행되지 않습니다
                                            </p>
                                        </div>
                                    )}

                                    <div style={securityInfoStyle}>
                                        <FontAwesomeIcon icon={faShieldAlt} style={{color: '#22c55e', marginRight: '8px'}} />
                                        <span>SSL 보안 연결로 안전하게 보호됩니다</span>
                                    </div>
                                </div>
                            )}

                            {step === 4 && (
                                <div style={stepContentStyle}>
                                    <h2 style={stepTitleStyle}>기부 내역을 확인해주세요</h2>
                                    <p style={stepDescStyle}>입력하신 정보가 정확한지 마지막으로 확인해주세요</p>
                                    
                                    <div style={summaryCardStyle}>
                                        <div style={summaryHeaderStyle}>
                                            <h3>기부 요약</h3>
                                        </div>
                                        
                                        <div style={summaryBodyStyle}>
                                            <div style={summaryRowStyle}>
                                                <span>캠페인</span>
                                                <span style={summaryValueStyle}>{campaign.title}</span>
                                            </div>
                                            <div style={summaryRowStyle}>
                                                <span>기부 금액</span>
                                                <span style={{...summaryValueStyle, fontWeight: 'bold', color: '#dc2626'}}>
                                                    {formatCurrency(getCurrentAmount())}
                                                </span>
                                            </div>
                                            <div style={summaryRowStyle}>
                                                <span>기부 유형</span>
                                                <span style={summaryValueStyle}>
                                                    {donationData.isRecurring ? '정기후원 (월)' : '일시 기부'}
                                                </span>
                                            </div>
                                            <div style={summaryRowStyle}>
                                                <span>기부자명</span>
                                                <span style={summaryValueStyle}>
                                                    {donationData.isAnonymous ? '익명' : donationData.donorName}
                                                </span>
                                            </div>
                                            <div style={summaryRowStyle}>
                                                <span>결제 방법</span>
                                                <span style={summaryValueStyle}>
                                                    {donationData.paymentMethod === 'bank' && '계좌이체 (무통장 입금)'}
                                                    {donationData.paymentMethod === 'mobile' && '간편결제 (카카오페이/토스페이)'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* <div style={benefitInfoStyle}>
                                        <h4 style={benefitTitleStyle}>
                                            <FontAwesomeIcon icon={faCertificate} style={{marginRight: '8px', color: '#f59e0b'}} />
                                            세액공제 혜택
                                        </h4>
                                        <p style={benefitDescStyle}>
                                            기부금은 연말정산 시 세액공제 대상입니다. 
                                            {formatCurrency(Math.floor(getCurrentAmount() * 0.15))}의 세금을 절약할 수 있어요!
                                        </p>
                                    </div> */}

                                    <div style={agreementStyle}>
                                        <label style={checkboxLabelStyle}>
                                            <input type="checkbox" required style={checkboxStyle} />
                                            <span style={checkboxTextStyle}>
                                                개인정보 수집·이용에 동의합니다 (필수)
                                            </span>
                                        </label>
                                        <label style={checkboxLabelStyle}>
                                            <input type="checkbox" style={checkboxStyle} />
                                            <span style={checkboxTextStyle}>
                                                기부 소식 및 활동 업데이트 수신 동의 (선택)
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {step === 5 && (
                                <div style={{...stepContentStyle, textAlign: 'center'}}>
                                    <div style={successIconStyle}>
                                        <FontAwesomeIcon icon={faHeart} />
                                    </div>
                                    <h2 style={successTitleStyle}>기부 신청이 완료되었습니다!</h2>
                                    <p style={successDescStyle}>
                                        {donationData.donorName}님의 {formatCurrency(getCurrentAmount())} 기부 신청을 접수하였습니다.
                                    </p>
                                    
                                    {donationData.paymentMethod === 'bank' && (
                                        <div style={bankTransferGuideStyle}>
                                            <h4 style={{fontWeight: '600', marginBottom: '1rem', color: '#1e293b'}}>
                                                <FontAwesomeIcon icon={faBank} style={{marginRight: '8px', color: '#a65353'}} />
                                                입금 안내
                                            </h4>
                                            <div style={bankDetailsCardStyle}>
                                                <div style={bankDetailRowStyle}>
                                                    <span style={bankLabelStyle}>은행명:</span>
                                                    <span style={bankValueStyle}>국민은행</span>
                                                </div>
                                                <div style={bankDetailRowStyle}>
                                                    <span style={bankLabelStyle}>계좌번호:</span>
                                                    <span style={bankValueStyle}>268801-04-262502</span>
                                                </div>
                                                <div style={bankDetailRowStyle}>
                                                    <span style={bankLabelStyle}>예금주:</span>
                                                    <span style={bankValueStyle}>한국외상센터지원단체</span>
                                                </div>
                                                <div style={bankDetailRowStyle}>
                                                    <span style={bankLabelStyle}>입금자명:</span>
                                                    <span style={bankValueStyle}>{donationData.donorName}</span>
                                                </div>
                                                <div style={{...bankDetailRowStyle, borderBottom: 'none', marginTop: '1rem'}}>
                                                    <span style={bankLabelStyle}>입금금액:</span>
                                                    <span style={{...bankValueStyle, fontSize: '1.2rem', fontWeight: 'bold', color: '#dc2626'}}>
                                                        {formatCurrency(getCurrentAmount())}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div style={processingInfoStyle}>
                                        <div style={processingIconStyle}>
                                            <FontAwesomeIcon icon={faClock} />
                                        </div>
                                        <h3 style={processingTitleStyle}>기부 확인 안내</h3>
                                        <p style={processingDescStyle}>
                                            입금 확인은 <strong>2~10시간 정도</strong> 소요됩니다.<br/>
                                            확인 완료 후 <strong>기부증서를 이메일로</strong> 보내드립니다.<br/>
                                            소중한 기부에 진심으로 감사드립니다.
                                        </p>
                                        
                                        <div style={contactInfoStyle}>
                                            <p style={contactTextStyle}>
                                                문의사항이 있으시면 <strong>contact@tchelp.org</strong>로 연락주세요.
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div style={transactionInfoStyle}>
                                        <p style={{fontSize: '12px', color: '#000000'}}>
                                            접수번호: {sessionStorage.getItem('lastTransactionId') || 'REQ' + Date.now()}
                                        </p>
                                    </div>
                                    
                                    <div style={successActionsStyle}>
                                        <button 
                                            onClick={() => navigate('/')}
                                            style={primaryButtonStyle}
                                        >
                                            홈으로 돌아가기
                                        </button>
                                        <button 
                                            onClick={() => navigate('/campaigns')}
                                            style={secondaryButtonStyle}
                                        >
                                            다른 캠페인 보기
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Navigation Buttons */}
                            {step < 5 && (
                                <div style={navigationStyle}>
                                    {step > 1 && (
                                        <button onClick={handlePrev} style={secondaryButtonStyle}>
                                            이전
                                        </button>
                                    )}
                                    
                                    <div style={{flex: 1}}></div>
                                    
                                    {step < 4 ? (
                                        <button 
                                            onClick={handleNext}
                                            disabled={
                                                (step === 1 && !canProceedStep1) ||
                                                (step === 2 && !canProceedStep2) ||
                                                (step === 3 && !canProceedStep3)
                                            }
                                            style={{
                                                ...primaryButtonStyle,
                                                ...(
                                                    (step === 1 && !canProceedStep1) ||
                                                    (step === 2 && !canProceedStep2) ||
                                                    (step === 3 && !canProceedStep3)
                                                    ? disabledButtonStyle : {}
                                                )
                                            }}
                                        >
                                            다음
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={handleSubmit}
                                            style={primaryButtonStyle}
                                        >
                                            <FontAwesomeIcon icon={faHeart} style={{marginRight: '8px'}} />
                                            기부 신청하기
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

// Styles
const pageStyle = {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
};

const headerStyle = {
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    padding: '1rem 0',
    position: 'sticky',
    top: 0,
    zIndex: 100
};

const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
};

const backButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s'
};

const mainStyle = {
    padding: '2rem 0'
};

const mainContainerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    boxSizing: 'border-box'
};

const sidebarColumnStyle = {
    position: 'sticky',
    top: '73px',
    alignSelf: 'stretch',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column'
};

const sidebarColumnNonStickyStyle = {
    position: 'static'
};

const sidebarPanelStyle = {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '2rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%'
};

const campaignTopWrapStyle = {
    width: '100%',
    marginBottom: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
};

const stepsStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '1rem 1.25rem',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: 'auto',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e2e8f0',
    flexShrink: 0
};

const stepStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: '#94a3b8'
};

const activeStepStyle = {
    color: '#a65353'
};

const completedStepStyle = {
    color: '#22c55e'
};

const stepNumberStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 'bold'
};

const stepLabelStyle = {
    fontSize: '14px',
    fontWeight: '500'
};

const contentStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '3rem',
    alignItems: 'stretch',
    width: '100%'
};

const campaignCardInnerStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
};

const campaignHeaderStyle = {
    marginBottom: '1.5rem'
};

const urgencyBadgeStyle = {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '8px',
    display: 'inline-block'
};

const campaignTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '4px'
};

const hospitalNameStyle = {
    color: '#64748b',
    fontSize: '14px'
};

const progressSectionStyle = {
    marginBottom: '1.5rem'
};

const progressInfoStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px'
};

const currentAmountStyle = {
    fontWeight: '700',
    color: '#22c55e'
};

const targetAmountStyle = {
    color: '#64748b',
    fontSize: '14px'
};

const progressBarContainerStyle = {
    backgroundColor: '#e2e8f0',
    borderRadius: '6px',
    height: '8px',
    marginBottom: '8px'
};

const progressBarStyle = {
    backgroundColor: '#22c55e',
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.3s ease'
};

const progressTextStyle = {
    fontSize: '12px',
    color: '#64748b',
    textAlign: 'center'
};

const impactBoxStyle = {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    color: '#166534'
};

const formSectionStyle = {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '2rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    minHeight: '100%'
};

const stepContentStyle = {
    marginBottom: '2rem'
};

const stepTitleStyle = {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '0.5rem'
};

const stepDescStyle = {
    color: '#64748b',
    marginBottom: '2rem'
};

const amountGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem'
};

const amountButtonStyle = {
    padding: '1.5rem',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent'
};

const selectedAmountStyle = {
    border: '2px solid #a65353',
    backgroundColor: '#f0f4ff'
};

const amountValueStyle = {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#1e293b'
};

const amountImpactStyle = {
    fontSize: '12px',
    color: '#64748b'
};

const customAmountStyle = {
    position: 'relative',
    marginBottom: '1.5rem'
};

const customInputStyle = {
    width: '100%',
    padding: '1rem',
    paddingRight: '3rem',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '1.125rem',
    outline: 'none',
    transition: 'border-color 0.2s'
};

const currencyLabelStyle = {
    position: 'absolute',
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: '500'
};

const recurringOptionStyle = {
    marginTop: '1rem'
};

const checkboxLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    marginBottom: '12px'
};

const checkboxStyle = {
    width: '18px',
    height: '18px',
    accentColor: '#a65353'
};

const checkboxTextStyle = {
    color: '#374151',
    fontSize: '14px'
};

const checkboxSubtextStyle = {
    color: '#9ca3af'
};

const formGroupStyle = {
    marginBottom: '1.5rem'
};

const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    color: '#374151',
    fontWeight: '500',
    fontSize: '14px'
};

const iconStyle = {
    color: '#a65353',
    width: '16px'
};

const inputStyle = {
    width: '100%',
    padding: '12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s'
};

const anonymousOptionStyle = {
    marginBottom: '2rem'
};

const commemorativeStyle = {
    marginBottom: '1.5rem'
};

const subTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '1rem'
};

const commemorativeGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem'
};

const selectStyle = {
    width: '100%',
    padding: '12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: 'white',
    outline: 'none'
};

const dateInputStyle = {
    width: '100%',
    padding: '12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none'
};

const textareaStyle = {
    width: '100%',
    padding: '12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit'
};

const paymentGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem'
};

const paymentMethodStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1.5rem',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
    gap: '8px',
    position: 'relative'
};

const selectedPaymentStyle = {
    borderColor: '#a65353',
    backgroundColor: '#f0f4ff'
};

const paymentIconStyle = {
    fontSize: '2rem',
    color: '#a65353',
    marginBottom: '8px'
};

const paymentLabelStyle = {
    fontWeight: '600',
    color: '#1e293b'
};

const paymentDescStyle = {
    fontSize: '12px',
    color: '#64748b'
};

const securityInfoStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#166534'
};

const summaryCardStyle = {
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    marginBottom: '1.5rem',
    overflow: 'hidden'
};

const summaryHeaderStyle = {
    backgroundColor: '#f8fafc',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e2e8f0'
};

const summaryBodyStyle = {
    padding: '1.5rem'
};

const summaryRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    fontSize: '14px'
};

const summaryValueStyle = {
    color: '#1e293b',
    fontWeight: '500'
};

const benefitInfoStyle = {
    backgroundColor: '#fffbeb',
    border: '1px solid #fed7aa',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem'
};

const benefitTitleStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#92400e',
    marginBottom: '4px'
};

const benefitDescStyle = {
    fontSize: '13px',
    color: '#92400e',
    lineHeight: 1.4
};

const agreementStyle = {
    marginTop: '1rem'
};

const navigationStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    paddingTop: '2rem',
    borderTop: '1px solid #e2e8f0'
};

const primaryButtonStyle = {
    backgroundColor: '#a65353',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px'
};

const secondaryButtonStyle = {
    backgroundColor: 'transparent',
    color: '#64748b',
    border: '2px solid #e2e8f0',
    padding: '12px 24px',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px'
};

const disabledButtonStyle = {
    backgroundColor: '#e2e8f0',
    color: '#94a3b8',
    cursor: 'not-allowed'
};

const successIconStyle = {
    fontSize: '4rem',
    color: '#22c55e',
    marginBottom: '1rem'
};

const successTitleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '1rem'
};

const successDescStyle = {
    color: '#64748b',
    marginBottom: '2rem',
    fontSize: '1.125rem'
};

const successActionsStyle = {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center'
};

const loadingStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '1rem'
};

const spinnerStyle = {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTop: '3px solid #a65353',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
};

const errorStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '1rem'
};

const linkStyle = {
    color: '#a65353',
    textDecoration: 'none',
    fontWeight: '600'
};

const paymentBadgeStyle = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '700',
    textTransform: 'uppercase'
};

const bankInfoStyle = {
    marginTop: '2rem',
    padding: '1.5rem',
    backgroundColor: '#f0f4ff',
    borderRadius: '12px',
    border: '2px solid #a65353'
};

const bankInfoTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center'
};

const bankDetailsStyle = {
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem'
};


const bankTransferGuideStyle = {
    backgroundColor: '#f0f4ff',
    border: '2px solid #a65353',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '2rem'
};


const bankDetailsCardStyle = {
    backgroundColor: 'white',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    marginBottom: '1rem'
};

const bankDetailRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f1f5f9'
};

const bankLabelStyle = {
    color: '#64748b',
    fontSize: '14px'
};

const bankValueStyle = {
    color: '#1e293b',
    fontWeight: '600',
    fontSize: '14px'
};

const bankNoteStyle = {
    fontSize: '12px',
    color: '#64748b',
    lineHeight: '1.5'
};

const mobilePaymentStyle = {
    marginTop: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fffbeb',
    borderRadius: '12px',
    border: '2px solid #f59e0b'
};

const mobilePaymentTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '1rem'
};

const mobilePaymentGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
    marginBottom: '1rem'
};

const mobilePaymentButtonStyle = {
    padding: '1rem',
    backgroundColor: 'white',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
};

const mobilePaymentNoteStyle = {
    fontSize: '12px',
    color: '#92400e',
    lineHeight: '1.5'
};

const transactionInfoStyle = {
    margin: '1rem 0',
    padding: '1rem',
    backgroundColor: '#f8fafc',
    borderRadius: '8px'
};

const processingInfoStyle = {
    backgroundColor: '#f0f9ff',
    border: '2px solid #0ea5e9',
    borderRadius: '12px',
    padding: '2rem',
    margin: '2rem 0',
    textAlign: 'center'
};

const processingIconStyle = {
    fontSize: '2.5rem',
    color: '#0ea5e9',
    marginBottom: '1rem'
};

const processingTitleStyle = {
    fontSize: '1.3rem',
    fontWeight: '600',
    color: '#0c4a6e',
    marginBottom: '1rem'
};

const processingDescStyle = {
    color: '#075985',
    lineHeight: '1.6',
    marginBottom: '1rem'
};

const contactInfoStyle = {
    backgroundColor: 'rgba(255,255,255,0.7)',
    borderRadius: '8px',
    padding: '1rem',
    marginTop: '1rem'
};

const contactTextStyle = {
    fontSize: '14px',
    color: '#0c4a6e',
    margin: 0
};

export default DonatePage;
                                            